import { FormEvent, useState } from 'react';
import { Link, useRouter, useSearch } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, Eye, EyeOff, Lock } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { useToast } from '@/shared/ui/toast-provider';
import { resolveErrorMessage } from '@/shared/lib/errors';
import { postAuthResetPassword } from '@/entities/auth';

const backgroundImage =
  'https://images.unsplash.com/photo-1497366858526-0766cadbe8fa?q=80&w=2069&auto=format&fit=crop';

export const ResetPasswordPage = () => {
  const router = useRouter();
  const toast = useToast();
  const { token } = useSearch({
    from: '/(auth)/reset-password',
  });
  const hasToken = Boolean(token && token.length > 0);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const resetPasswordMutation = useMutation({
    mutationFn: postAuthResetPassword,
    onSuccess: () => {
      toast({
        type: 'success',
        title: 'Password updated',
        description: 'You can now sign in with your new password.',
      });
      router.navigate({
        to: '/sign-in',
        search: { redirect: undefined },
      });
    },
    onError: (mutationError) => {
      const message = resolveErrorMessage(
        mutationError,
        'We could not reset your password. Please try again.',
      );
      setError(message);
      toast({
        type: 'error',
        title: 'Reset failed',
        description: message,
      });
    },
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!hasToken) {
      setError(
        'Password reset token is missing. Check the link in your email.',
      );
      return;
    }

    if (!newPassword.trim() || !confirmPassword.trim()) {
      setError('Please enter and confirm your new password.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setError(null);
    try {
      await resetPasswordMutation.mutateAsync({
        token: token as string,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
    } catch {
      // handled in onError
    }
  };

  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-950 px-4 py-10 text-slate-50">
      <img
        src={backgroundImage}
        alt="Modern workspace background"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-slate-950/65" aria-hidden />

      <Link
        to="/sign-in"
        search={{ redirect: undefined }}
        className="group absolute left-6 top-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-slate-950/60 px-4 py-2 text-sm font-medium text-slate-200 shadow-lg shadow-slate-950/40 backdrop-blur transition hover:bg-slate-900/80 hover:text-white"
      >
        <ArrowLeft className="h-4 w-4 transition group-hover:-translate-x-0.5" />
        Back
      </Link>

      <section className="relative z-10 w-full max-w-[420px] space-y-8 rounded-[32px] border border-white/10 bg-slate-900/75 p-10 text-slate-50 shadow-[0_30px_80px_rgba(10,17,33,0.55)] backdrop-blur-2xl">
        <header className="space-y-3 text-center">
          <h1 className="text-3xl font-semibold tracking-tight text-white">
            Reset Password
          </h1>
          <p className="text-sm text-slate-200/80">
            Please enter your new password
          </p>
        </header>

        <form className="space-y-5" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <label htmlFor="password" className="sr-only">
              New password
            </label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" />
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="New Password"
                value={newPassword}
                onChange={(event) => {
                  if (error) setError(null);
                  setNewPassword(event.target.value);
                }}
                className="h-12 rounded-2xl border-white/20 bg-white/10 pr-12 pl-12 text-base text-slate-100 placeholder:text-slate-200/60 focus-visible:ring-blue-500/60 focus-visible:ring-offset-0"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-slate-200/80 transition hover:bg-white/10 hover:text-white"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="confirm-password" className="sr-only">
              Confirm password
            </label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" />
              <Input
                id="confirm-password"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(event) => {
                  if (error) setError(null);
                  setConfirmPassword(event.target.value);
                }}
                className="h-12 rounded-2xl border-white/20 bg-white/10 pr-12 pl-12 text-base text-slate-100 placeholder:text-slate-200/60 focus-visible:ring-blue-500/60 focus-visible:ring-offset-0"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-slate-200/80 transition hover:bg-white/10 hover:text-white"
                aria-label={
                  showConfirmPassword ? 'Hide password' : 'Show password'
                }
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          {error ? (
            <p className="text-sm font-semibold text-amber-300">{error}</p>
          ) : null}

          {!hasToken ? (
            <p className="text-sm font-semibold text-amber-300">
              No reset token was provided. Please use the link from your email.
            </p>
          ) : null}

          <Button
            type="submit"
            variant="glow"
            size="lg"
            className="w-full gap-2 text-base"
            disabled={!hasToken || resetPasswordMutation.isPending}
          >
            {resetPasswordMutation.isPending
              ? 'Updating password...'
              : 'Reset Password'}
            {!resetPasswordMutation.isPending ? (
              <ArrowRight className="h-4 w-4" />
            ) : null}
          </Button>
        </form>
      </section>
    </main>
  );
};
